import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSettings } from "@/contexts/SettingsContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Stethoscope, Loader2, UserCheck, UserX, Download, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";
import usePersistentState from "@/hooks/usePersistentState";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function exportCsv(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function LearnerList() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const label = settings.learner_label || "Learner";
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = usePersistentState("learners:search", "");
  const [statusFilter, setStatusFilter] = usePersistentState("learners:status", "active");
  const [cohortFilter, setCohortFilter] = usePersistentState("learners:cohort", "all");
  const [cohorts, setCohorts] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [exportStatus, setExportStatus] = useState("both");
  const [exporting, setExporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const handleDelete = async () => {
    const l = deleteTarget;
    if (!l) return;
    try {
      await axios.delete(`${API}/learners/${l.id}`, { withCredentials: true });
      setSelectedIds((prev) => {
        if (!prev.has(l.id)) return prev;
        const next = new Set(prev);
        next.delete(l.id);
        return next;
      });
      fetchLearners();
    } catch (err) {
      throw err;
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const { data } = await axios.post(
        `${API}/learners/bulk-delete`,
        { learner_ids: ids },
        { withCredentials: true },
      );
      const deleted = data?.deleted_count ?? ids.length;
      const missing = (data?.not_found || []).length;
      setSelectedIds(new Set());
      fetchLearners();
      toast.success(
        `Deleted ${deleted} ${label.toLowerCase()}${deleted === 1 ? "" : "s"}` +
          (missing ? ` (${missing} not found)` : ""),
      );
    } catch (err) {
      throw err;
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = learners.length > 0 && learners.every((l) => selectedIds.has(l.id));
  const someVisibleSelected = !allVisibleSelected && learners.some((l) => selectedIds.has(l.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      // Deselect only the currently-visible rows; preserve any others.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        learners.forEach((l) => next.delete(l.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        learners.forEach((l) => next.add(l.id));
        return next;
      });
    }
  };

  // Load cohorts for filter dropdown (all statuses so learners in archived cohorts can still be filtered)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cohorts`, { withCredentials: true });
        setCohorts(data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const fetchLearners = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (cohortFilter !== "all") params.cohort_id = cohortFilter;
      const { data } = await axios.get(`${API}/learners`, {
        params,
        withCredentials: true,
      });
      setLearners(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, cohortFilter]);

  useEffect(() => {
    const timer = setTimeout(fetchLearners, 300);
    return () => clearTimeout(timer);
  }, [fetchLearners]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportStatus !== "both") params.status = exportStatus;
      const { data } = await axios.get(`${API}/learners`, { params, withCredentials: true });
      const rows = data.map((c) => ({
        "First Name": c.first_name || "",
        "Last Name": c.last_name || "",
        "Email": c.email || "",
        "Status": c.status || "",
        "Cohort(s)": c.cohort_name || "None",
      }));
      if (rows.length === 0) {
        alert("No learners to export with the selected filter.");
      } else {
        exportCsv(rows, `learners_${exportStatus}_${new Date().toISOString().slice(0, 10)}.csv`);
      }
      setShowExport(false);
    } catch {
      alert("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="learner-list-page">
      <div>
        <h2
          className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight"
          data-testid="learner-list-title"
        >
          {label} Management
        </h2>
        <p className="text-[#6E7787] font-body text-sm mt-1">
          All {label.toLowerCase()}s across all cohorts
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 items-start sm:items-center">
        <div className="flex gap-1 bg-white border rounded-lg p-1" style={{ borderColor: "#e2e6ef" }} data-testid="learner-status-filter">
          {[{ value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }].map((tab) => (
            <button
              key={tab.value}
              onClick={() => { setLoading(true); setStatusFilter(tab.value); }}
              data-testid={`learner-filter-${tab.value}`}
              className={`px-3 py-1.5 rounded-md text-sm font-body font-medium transition-all ${
                statusFilter === tab.value
                  ? ""
                  : "hover:bg-black/[0.03]"
              }`}
              style={statusFilter === tab.value
                ? { backgroundColor: "#062546", color: "#ffffff" }
                : { color: "#5a6577" }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#8896a7" }} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${label.toLowerCase()}s by name or email...`}
          data-testid="learner-search"
          className="pl-9 bg-white h-9 text-sm font-body"
          style={{ borderColor: "#e2e6ef", color: "#062546" }}
        />
        </div>
        <Select value={cohortFilter} onValueChange={(v) => { setLoading(true); setCohortFilter(v); }}>
          <SelectTrigger
            data-testid="learner-cohort-filter"
            className="h-9 w-full sm:w-auto sm:min-w-[220px] sm:max-w-[420px] bg-white text-sm font-body focus:ring-0"
            style={{ borderColor: "#e2e6ef", color: "#062546" }}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Users className="h-4 w-4 shrink-0" style={{ color: "#8896a7" }} />
              <SelectValue placeholder="All Cohorts" />
            </div>
          </SelectTrigger>
          <SelectContent
            className="bg-white max-h-[320px]"
            style={{ borderColor: "#e2e6ef", color: "#062546" }}
          >
            <SelectItem value="all" data-testid="cohort-filter-all" className="font-body text-sm focus:bg-[#f3f5f9] focus:text-[#062546]">All Cohorts</SelectItem>
            {cohorts.map((co) => (
              <SelectItem
                key={co.id}
                value={co.id}
                data-testid={`cohort-filter-${co.id}`}
                className="font-body text-sm focus:bg-[#f3f5f9] focus:text-[#062546]"
              >
                {co.name}
                {co.status && co.status !== "active" && (
                  <span className="ml-2 text-[10px] uppercase" style={{ color: "#8896a7" }}>{co.status}</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowExport(true)}
          data-testid="export-learners-btn"
          className="bg-white text-sm h-9 px-4 shrink-0 font-body"
          style={{ borderColor: "#e2e6ef", color: "#5a6577" }}
        >
          <Download className="h-4 w-4 mr-1.5" />Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
        </div>
      ) : learners.length === 0 ? (
        <div className="bg-[#11141A] border border-dashed border-white/10 rounded-xl p-12 text-center" data-testid="learner-empty-state">
          <div
            className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 10%, transparent)" }}
          >
            <Stethoscope className="h-7 w-7" style={{ color: "var(--primary-color, #4A90D9)" }} />
          </div>
          <h3 className="font-body text-lg font-bold text-white mb-2">
            {search ? `No matching ${label.toLowerCase()}s` : `No ${label.toLowerCase()}s yet`}
          </h3>
          <p className="text-[#6E7787] font-body text-sm max-w-sm mx-auto">
            {search
              ? "Try adjusting your search."
              : `${label}s will appear here once added to a cohort.`}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="learner-table">
          <div className="hidden sm:grid grid-cols-[24px_repeat(12,minmax(0,1fr))] gap-4 items-center px-4 py-2 text-xs text-[#6E7787] font-body uppercase tracking-wider">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
              onChange={toggleSelectAll}
              data-testid="learner-select-all"
              aria-label="Select all visible learners"
              className="h-4 w-4 rounded border-white/20 bg-transparent accent-[var(--primary-color,#4A90D9)] cursor-pointer"
            />
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-3">Active Cohort</div>
            <div className="col-span-3">Status</div>
          </div>
          {learners.map((c) => {
            const isSelected = selectedIds.has(c.id);
            return (
            <div
              key={c.id}
              onClick={() => navigate(`/admin/learners/${c.id}`)}
              className={`bg-[#11141A] border rounded-lg px-4 py-3.5 cursor-pointer hover:border-white/20 transition-all group ${
                isSelected ? "border-[var(--primary-color,#4A90D9)]/60 ring-1 ring-[var(--primary-color,#4A90D9)]/30" : "border-white/10"
              }`}
              data-testid={`learner-row-${c.id}`}
            >
              <div className="sm:grid sm:grid-cols-[24px_repeat(12,minmax(0,1fr))] sm:gap-4 sm:items-center">
                <div className="hidden sm:flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`learner-select-${c.id}`}
                    aria-label={`Select ${c.first_name} ${c.last_name}`}
                    className="h-4 w-4 rounded border-white/20 bg-transparent accent-[var(--primary-color,#4A90D9)] cursor-pointer"
                  />
                </div>
                <div className="col-span-3 flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`learner-select-mobile-${c.id}`}
                    aria-label={`Select ${c.first_name} ${c.last_name}`}
                    className="sm:hidden h-4 w-4 rounded border-white/20 bg-transparent accent-[var(--primary-color,#4A90D9)] cursor-pointer"
                  />
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white shrink-0"
                    style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                  >
                    {c.first_name?.[0]}{c.last_name?.[0]}
                  </div>
                  <span className="text-sm font-body font-medium text-white truncate">
                    {c.first_name} {c.last_name}
                  </span>
                  </div>
                <div className="col-span-3 text-sm text-[#A1A9B8] font-body mt-1 sm:mt-0 truncate">
                  {c.email}
                </div>
                <div className="col-span-3 text-sm font-body mt-1 sm:mt-0">
                  {c.cohort_name ? (
                    <span className="text-white">{c.cohort_name}</span>
                  ) : (
                    <span className="text-[#6E7787]">None</span>
                  )}
                </div>
                <div className="col-span-3 mt-1 sm:mt-0 flex items-center justify-between gap-2">
                  <Badge
                    data-testid={`learner-status-${c.id}`}
                    className={
                      c.status === "active"
                        ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                        : c.status === "merged"
                        ? "bg-purple-500/15 text-purple-400 border-purple-500/20"
                        : "bg-red-500/15 text-red-400 border-red-500/20"
                    }
                  >
                    {c.status === "active" ? "Active" : c.status === "merged" ? "Merged" : "Inactive"}
                  </Badge>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                    data-testid={`learner-delete-${c.id}`}
                    title="Delete learner"
                    className="p-1.5 text-[#6E7787] hover:text-red-400 rounded-md hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Export Modal */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-sm" data-testid="export-modal">
          <DialogHeader>
            <DialogTitle className="font-body font-bold text-lg text-white">Export Learners</DialogTitle>
            <DialogDescription className="text-[#6E7787] font-body text-sm">
              Choose which learners to include in the CSV export.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#A1A9B8] font-body font-medium">Include learners with status:</p>
            <div className="flex gap-2">
              {[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "both", label: "Both" }].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setExportStatus(opt.value)}
                  data-testid={`export-status-${opt.value}`}
                  className={`px-4 py-2.5 text-sm font-body rounded-lg border transition-colors flex-1 ${
                    exportStatus === opt.value
                      ? "border-white/20 bg-white/10 text-white"
                      : "border-white/10 text-[#6E7787] hover:text-white hover:border-white/15"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#6E7787] font-body">
              The export will include: Name, Email, Status, and Cohort(s).
            </p>
          </div>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setShowExport(false)} className="border-white/10 text-[#A1A9B8] hover:text-white" data-testid="export-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              data-testid="export-confirm"
              className="text-white font-body hover:opacity-90"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={3}
        entityName={deleteTarget ? `${deleteTarget.first_name} ${deleteTarget.last_name}` : ""}
        entityType="learner"
        entityId={deleteTarget?.id}
        description="Permanently delete this learner and all their responses, scores, and activity. This cannot be undone."
        onConfirm={handleDelete}
      />

      {/* Bulk selection action bar — sticky footer when 1+ learners selected. */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#11141A] border border-white/15 shadow-xl rounded-full pl-5 pr-2 py-2 flex items-center gap-4"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-body text-white" data-testid="bulk-selected-count">
            <span className="font-semibold">{selectedIds.size}</span> selected
          </span>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            data-testid="bulk-clear-selection"
            className="text-xs font-body text-[#A1A9B8] hover:text-white px-2 py-1 rounded-full hover:bg-white/5"
          >
            Clear
          </button>
          <Button
            size="sm"
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="bulk-delete-btn"
            className="bg-red-600 hover:bg-red-700 text-white font-body rounded-full h-9 px-4"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Delete selected
          </Button>
        </div>
      )}

      <DeleteConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        tier={3}
        entityName={`${selectedIds.size} ${label.toLowerCase()}${selectedIds.size === 1 ? "" : "s"}`}
        entityType="learner"
        confirmLabel={`Delete ${selectedIds.size} permanently`}
        description={`Permanently delete ${selectedIds.size} selected ${label.toLowerCase()}${selectedIds.size === 1 ? "" : "s"} and all their responses, scores, and activity. This cannot be undone.`}
        onConfirm={handleBulkDelete}
      />
    </div>
  );
}
